/** Settings → Store: currency, order numbering, emails, units, checkout. Prop-less; uses useSite(). */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Collapse, Form, Input, InputNumber, Row, Select, Space, Spin, Switch, Tooltip, message } from 'antd';
import { useTranslation } from '../../../shared/i18n';
import { CURRENCIES, Hint, listOf, toastErr, useSite } from './common';
import { QUIET } from './api';
import { MoneyInput } from './ShopCommon';

const ShopSettings = () => {
    const { t } = useTranslation();
    const { api, shop, reloadShop } = useSite();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [pushing, setPushing] = useState(false);
    const [currencies, setCurrencies] = useState(CURRENCIES);
    const [supported, setSupported] = useState([]);
    const extra = Form.useWatch('currencies', form) || [];
    const base = Form.useWatch('currency', form);

    useEffect(() => {
        if (!shop) return;
        form.setFieldsValue({
            currency: shop.currency || 'USD', order_prefix: shop.order_prefix ?? '#', order_email: shop.order_email || '', notify_email: shop.notify_email || '',
            free_shipping_threshold: shop.free_shipping_threshold ?? null, weight_unit: shop.weight_unit || 'kg', blog_title: shop.blog_title || '', next_order_number: shop.next_order_number,
            require_phone: !!shop.checkout?.require_phone, terms_url: shop.checkout?.terms_url || '',
        });
        form.setFieldsValue({
            currencies: (shop.currencies || []).filter((c) => c !== shop.currency),
            currency_rates: Object.fromEntries(Object.entries(shop.currency_rates || {}).map(([k, v]) => [k, v === null || v === '' ? undefined : Number(v)])),
            rounding: shop.rounding || 'none',
        });
        if (shop.currency && !CURRENCIES.includes(shop.currency)) setCurrencies([...CURRENCIES, shop.currency]);
    }, [shop, form]);
    useEffect(() => {
        api.get('/currencies', null, QUIET).then((d) => {
            const items = listOf(d);
            if (items.length) { setSupported(items); setCurrencies(items.map((x) => x.code)); }
        }).catch(() => {});
    }, [api, shop?.currency]);

    const save = async () => {
        const v = await form.validateFields();
        const { require_phone, terms_url, next_order_number, currency_rates, ...rest } = v;
        rest.currencies = (rest.currencies || []).filter((c) => c !== rest.currency);
        rest.free_shipping_threshold = rest.free_shipping_threshold > 0 ? rest.free_shipping_threshold : null;
        rest.currency_rates = Object.fromEntries(Object.entries(currency_rates || {}).filter(([k, r]) => rest.currencies.includes(k) && r > 0).map(([k, r]) => [k, String(r)]));
        if (next_order_number && next_order_number !== shop.next_order_number) rest.next_order_number = next_order_number;
        setSaving(true);
        try {
            await api.put('/shop-settings', { ...rest, checkout: { ...(shop?.checkout || {}), require_phone, terms_url } });
            message.success(t('Saved'));
            reloadShop();
        } catch (e) { toastErr(e, t('Save failed')); } finally { setSaving(false); }
    };

    const push = async () => {
        setPushing(true);
        try { await api.post('/shop-settings/push'); message.success(t('Synced')); } catch (e) { toastErr(e); } finally { setPushing(false); }
    };

    if (!shop) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
    return (
        <Form form={form} layout="vertical" style={{ maxWidth: 720 }}>
            {shop.has_orders && <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('Changing the currency does not convert existing prices.')} />}
            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item name="currency" label={<span>{t('Store currency')}<Hint title={t('Prices and checkout use this currency')} /></span>}>
                        <Select showSearch options={currencies.map((c) => ({ value: c, label: c }))} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item name="weight_unit" label={t('Weight unit')}>
                        <Select options={['kg', 'g', 'lb', 'oz'].map((u) => ({ value: u, label: u }))} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}><Form.Item name="free_shipping_threshold" label={<span>{t('Free shipping over')}<Hint title={t('After discounts. Empty = off.')} /></span>}><MoneyInput currency={base || shop?.currency || 'USD'} /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="notify_email" label={t('New order notifications')} rules={[{ type: 'email' }]}><Input placeholder="you@example.com" /></Form.Item></Col>
                <Col xs={24} sm={12}><Form.Item name="order_email" label={<span>{t('Customer reply-to email')}<Hint title={t('Shown on order emails to customers')} /></span>} rules={[{ type: 'email' }]}><Input /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} sm={16}>
                    <Form.Item name="currencies" label={<span>{t('Also sell in')}<Hint title={t('Visitors can switch currency; prices are converted from the store currency')} /></span>}>
                        <Select mode="multiple" allowClear showSearch options={currencies.filter((c) => c !== base).map((c) => ({ value: c, label: c }))} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                    <Form.Item name="rounding" label={t('Rounding')}>
                        <Select options={[{ value: 'none', label: t('None') }, { value: 'whole', label: t('Whole number') }, { value: '99', label: '.99' }]} />
                    </Form.Item>
                </Col>
            </Row>
            {extra.length > 0 && (
                <Collapse ghost size="small" style={{ marginBottom: 8 }} items={[{
                    key: 'rates', label: t('Exchange rates'),
                    children: (
                        <Row gutter={16}>
                            {extra.map((c) => {
                                const auto = supported.find((x) => x.code === c)?.auto_rate;
                                return (
                                    <Col xs={24} sm={12} key={c}>
                                        <Form.Item name={['currency_rates', c]} label={`1 ${base || ''} = ? ${c}`}>
                                            <InputNumber min={0} step={0.0001} style={{ width: '100%' }} placeholder={auto ? `${t('Auto')}: ${Number(auto)}` : t('Auto')} />
                                        </Form.Item>
                                    </Col>
                                );
                            })}
                        </Row>
                    ),
                }]} />
            )}
            <Collapse style={{ marginBottom: 16 }} items={[{
                key: 'adv', label: t('Checkout and orders'),
                children: (
                    <Row gutter={16}>
                        <Col xs={24} sm={12}><Form.Item name="order_prefix" label={t('Order number prefix')}><Input maxLength={16} /></Form.Item></Col>
                        {shop.next_order_number !== undefined && <Col xs={24} sm={12}><Form.Item name="next_order_number" label={t('Next order number')}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>}
                        <Col xs={24} sm={12}><Form.Item name="blog_title" label={t('Blog title')}><Input maxLength={191} /></Form.Item></Col>
                        <Col xs={24} sm={12}><Form.Item name="terms_url" label={t('Terms of service URL')}><Input placeholder="/terms" /></Form.Item></Col>
                        <Col xs={24} sm={12}><Form.Item name="require_phone" label={t('Require phone number at checkout')} valuePropName="checked"><Switch /></Form.Item></Col>
                    </Row>
                ),
            }]} />
            <Space>
                <Button type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
                <Tooltip title={t('Products and posts sync automatically; use this if the live site looks out of date.')}>
                    <Button loading={pushing} onClick={push}>{t('Sync store to site')}</Button>
                </Tooltip>
            </Space>
        </Form>
    );
};

export default ShopSettings;
